// Centralized configuration for OpenAPI utilities and metrics

// OATS automatically injects backend URL when running in development mode
const OATS_BACKEND_BASE_URL = import.meta.env.VITE_OATS_BACKEND_BASE_URL

// API Base URLs
export const API_URLS = {
  BASE_API_URL: OATS_BACKEND_BASE_URL || 'https://api.tryloop.ai',
  BASE_API_LOCAL_URL: 'http://localhost:8000',
  BASE_API_HOST: OATS_BACKEND_BASE_URL ? new URL(OATS_BACKEND_BASE_URL).hostname : 'api.tryloop.ai',
  BASE_AGENT_URL: 'https://agents-service-983150008719.us-central1.run.app',
  TRUROI_AGENT_BASE_URL: 'https://truroi-agent-service-983150008719.us-central1.run.app',
  LOOP_SHEETS_AGENT_URL: import.meta.env.VITE_LOOP_SHEETS_AGENT_URL || 'https://loop-sheets-983150008719.us-central1.run.app',
  APP_SERVICE_BASE_URL: 'https://app-service-983150008719.us-central1.run.app',

  // Module-specific API URLs
  RECOVER_API_URL: OATS_BACKEND_BASE_URL || 'https://recover.tryloop.ai',
  GUARD_API_URL: OATS_BACKEND_BASE_URL || 'https://guard.tryloop.ai',
  BALANCE_API_URL: OATS_BACKEND_BASE_URL || 'https://balance.tryloop.ai',
  // TRUROI_API_URL: OATS_BACKEND_BASE_URL || 'https://truroi.tryloop.ai',

  // Other service URLs
  BASE_FIREBASE_FUNCTIONS_URL: 'https://functions.tryloop.ai',
  BASE_DOCS_URL: 'https://docs.tryloop.ai',
  BASE_AUTH_URL: 'https://auth.tryloop.ai',
  BASE_FRONTEND_URL: 'https://app.tryloop.ai'
} as const

// Module-specific API URL mapping for dynamic routing
export const MODULE_API_URL_MAP: Record<string, string> = {
  base_routes: API_URLS.BASE_API_URL,
  recover: API_URLS.RECOVER_API_URL,
  guard: API_URLS.GUARD_API_URL,
  balance: API_URLS.BALANCE_API_URL,
  truroi: API_URLS.BASE_API_URL,
  re_engage: API_URLS.BASE_API_URL
}

/**
 * Get the appropriate API URL for a given module
 * @param moduleKey The module identifier
 * @returns The API URL for the module
 */
export function getModuleApiUrl(moduleKey: string): string {
  if (!moduleKey) {
    console.warn('[API Router] No module key provided, using default')
    return API_URLS.BASE_API_URL
  }

  // In development with OATS, always use the OATS backend URL
  if (OATS_BACKEND_BASE_URL) {
    return OATS_BACKEND_BASE_URL
  }

  return MODULE_API_URL_MAP[moduleKey] || API_URLS.BASE_API_URL
}

/**
 * Configuration options for the metrics service
 */
export interface MetricsConfig {
  /** Enable metrics collection and reporting */
  enabled: boolean
  /** Enable HTTP request latency tracking */
  enableApiLatencyTracking: boolean
  /** Enable API error tracking (HTTP errors) */
  enableApiErrorTracking: boolean
  /** Enable performance metrics */
  enablePerformanceMetrics: boolean
  /** Enable general error tracking (JS errors, exceptions) */
  enableErrorTracking: boolean
  /** Enable user behavior tracking */
  enableBehaviorTracking: boolean
  /** Enable sending metrics to Slack channel */
  enableSlackMetrics: boolean
  /** Slack channel name for metrics notifications */
  slackChannel: string
  /** Batch size for sending metrics (number of metrics to accumulate before sending) */
  batchSize: number
  /** Flush interval in milliseconds (how often to send partial batches) */
  flushInterval: number
  /** Maximum batch size under high load */
  maxBatchSize: number
}

/**
 * Default metrics configuration
 */
export const DEFAULT_METRICS_CONFIG: MetricsConfig = {
  enabled: true,
  enableApiLatencyTracking: true,
  enableApiErrorTracking: true,
  enablePerformanceMetrics: false,
  enableErrorTracking: true,
  enableBehaviorTracking: false,
  enableSlackMetrics: false,
  slackChannel: 'fe-metrics',
  batchSize: 10,
  flushInterval: 5000, // 5 seconds
  maxBatchSize: 50
}

/**
 * Configuration options for OpenAPI utilities
 */
export interface OpenApiUtilsConfig {
  /** Enable API retry mechanism with exponential backoff */
  enableRetry: boolean

  /** Enable adding timestamp to requests to prevent caching */
  enableTimestamp: boolean

  /** Enable request abortion for duplicate requests */
  enableAbort: boolean
}

/**
 * Default configuration for OpenAPI utilities
 */
export const DEFAULT_CONFIG: OpenApiUtilsConfig = {
  enableRetry: false,
  enableTimestamp: false, // Disabled - timestamps pollute URLs and break error tracking
  enableAbort: true
}

/**
 * Current configuration for OpenAPI utilities
 * This can be modified at runtime to enable/disable features
 */
export let CURRENT_CONFIG: OpenApiUtilsConfig = { ...DEFAULT_CONFIG }

/**
 * Update the OpenAPI utilities configuration
 * @param config Partial configuration to update
 */
export function updateConfig(config: Partial<OpenApiUtilsConfig>): void {
  CURRENT_CONFIG = {
    ...CURRENT_CONFIG,
    ...config
  }
}

/**
 * Reset the OpenAPI utilities configuration to default values
 */
export function resetConfig(): void {
  CURRENT_CONFIG = { ...DEFAULT_CONFIG }
}
